import React, { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminRoute, UserRoute } from './components/ProtectedRoute';

// Components
import MainLayout from './components/layouts/MainLayout';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Admin from './components/Admin';
import UserPage from './components/UserPage';
import OrderPage from './components/OrderPage';
import Docs from './components/Docs';
import Payment from './components/Payment';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentError from './components/PaymentError';
import ConfiguratorAdmin from './components/admin/ConfiguratorAdmin';
import NotFound from './components/NotFound';
import Contact from './components/Contact';
import LegalPage from './components/LegalPage';

const MainLayoutWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, logout, isLoading } = useAuth();
  return (
    <MainLayout user={user} onLogout={logout} isLoading={isLoading}>
      {children}
    </MainLayout>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes - use MainLayoutWrapper to include both Header and Footer */}
          <Route path="/" element={<MainLayoutWrapper><Home /></MainLayoutWrapper>} />
          <Route path="/login" element={<MainLayoutWrapper><Login /></MainLayoutWrapper>} />
          <Route path="/register" element={<MainLayoutWrapper><Register /></MainLayoutWrapper>} />
          <Route path="/docs" element={<MainLayoutWrapper><Docs /></MainLayoutWrapper>} />
          <Route path="/contact" element={<MainLayoutWrapper><Contact /></MainLayoutWrapper>} />
          <Route path="/payment/success" element={<MainLayoutWrapper><PaymentSuccess /></MainLayoutWrapper>} />
          <Route path="/payment/error" element={<MainLayoutWrapper><PaymentError /></MainLayoutWrapper>} />
          <Route path="/privacy" element={<MainLayoutWrapper><LegalPage policyKey="privacy" /></MainLayoutWrapper>} />
          <Route path="/terms" element={<MainLayoutWrapper><LegalPage policyKey="terms" /></MainLayoutWrapper>} />
          <Route path="/refund" element={<MainLayoutWrapper><LegalPage policyKey="refund" /></MainLayoutWrapper>} />
          <Route path="/shipping" element={<MainLayoutWrapper><LegalPage policyKey="shipping" /></MainLayoutWrapper>} />
          <Route path="/cookies" element={<MainLayoutWrapper><LegalPage policyKey="cookies" /></MainLayoutWrapper>} />

          {/* User routes - any authenticated user - these should have Header and Footer too */}
          <Route element={<UserRoute />}>
            <Route path="/dashboard" element={<MainLayoutWrapper><Dashboard /></MainLayoutWrapper>} />
            <Route path="/payment" element={<MainLayoutWrapper><Payment /></MainLayoutWrapper>} />
          </Route>

          {/* Admin routes - only admins - these should have Header and Footer too */}
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/resources" element={<Admin />} />
            <Route path="/admin/configurator" element={<ConfiguratorAdmin />} />
            <Route path="/admin/user/:id" element={<UserPage />} />
            <Route path="/admin/order/:orderNumber" element={<OrderPage />} />
          </Route>

          {/* Catch-all route for 404 - use MainLayoutWrapper */}
          <Route path="*" element={<MainLayoutWrapper><NotFound /></MainLayoutWrapper>} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
